/**
 * Unified message grouping utility for all chat components.
 * Handles messages with different timestamp field names (createdAt, timestamp).
 */

// Generic MessageGroup type that works with any message type
export interface MessageGroup<T = unknown> {
  date: string;
  messages: T[];
}

// Options for grouping messages
export interface GroupMessagesOptions<T> {
  /** Function to extract the date header string from a message */
  getHeaderDate: (message: T) => string;
  /** How to sort the date groups. Default: 'dateKey' */
  sortDaysBy?: 'dateKey' | 'firstMessageCreatedAt';
  /** Function to extract the timestamp for sorting within a group. Default: uses createdAt */
  getTimestamp?: (message: T) => string | Date;
}

/**
 * Groups messages by date for display with date separators.
 * This is the canonical implementation used across admin, resident, and marketplace chats.
 *
 * @param messages - Array of messages to group
 * @param options - Configuration for grouping
 * @returns Array of MessageGroup objects
 *
 * @example
 * // For IMessage with createdAt field (admin/property chat):
 * groupMessages(messages, {
 *   getHeaderDate: (msg) => new Date(msg.createdAt).toDateString(),
 * });
 *
 * @example
 * // For ConversationMessage with timestamp field (marketplace):
 * groupMessages(messages, {
 *   getHeaderDate: (msg) => formatDate(msg.timestamp),
 *   getTimestamp: (msg) => msg.timestamp,
 * });
 */
export function groupMessages<T>(
  messages: T[] | undefined,
  options: GroupMessagesOptions<T>,
): MessageGroup<T>[] {
  if (!messages || messages.length === 0) return [];

  const { getHeaderDate, sortDaysBy = 'dateKey', getTimestamp } = options;

  // Group messages by date key
  const groups: Record<string, T[]> = {};
  messages.forEach((message) => {
    const key = getHeaderDate(message);
    if (!groups[key]) groups[key] = [];
    groups[key].push(message);
  });

  // Convert to array and sort messages within each group
  const mapped = Object.entries(groups).map(([date, groupedMessages]) => ({
    date,
    messages: groupedMessages.sort((a, b) => {
      const timeA = getTimestamp
        ? new Date(getTimestamp(a)).getTime()
        : new Date((a as any).createdAt || (a as any).timestamp).getTime();
      const timeB = getTimestamp
        ? new Date(getTimestamp(b)).getTime()
        : new Date((b as any).createdAt || (b as any).timestamp).getTime();
      return timeA - timeB;
    }),
  }));

  // Sort groups by date
  if (sortDaysBy === 'firstMessageCreatedAt') {
    return mapped.sort((a, b) => {
      const firstA = a.messages[0];
      const firstB = b.messages[0];
      const timeA = getTimestamp
        ? new Date(getTimestamp(firstA)).getTime()
        : new Date((firstA as any).createdAt || (firstA as any).timestamp || 0).getTime();
      const timeB = getTimestamp
        ? new Date(getTimestamp(firstB)).getTime()
        : new Date((firstB as any).createdAt || (firstB as any).timestamp || 0).getTime();
      return timeA - timeB;
    });
  }

  // Default: sort by the date key value
  return mapped.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

// Re-export for backwards compatibility
export default groupMessages;
